import {
  Archival as ArchivalMessage,
  Encounter as EncounterMessage,
  EncounterAbstract as EncounterAbstractMessage,
  EncounterDocument as EncounterDocumentMessage,
  EncounterDocumentSection as EncounterDocumentSectionMessage,
  EncounterDocumentStatus,
  EncounterDocumentType,
  EncounterFragment as EncounterFragmentMessage,
  EncounterPreview as EncounterPreviewMessage,
  EncounterStatus,
  FragmentStatus,
  RecordingMode,
  LiteratureReference as LiteratureReferenceMessage,
  PersonalInfo as PersonalInfoMessage,
  StoredAudio as StoredAudioMessage,
  Transcript as TranscriptMessage,
} from '@/shared/api/protocol-ts/model/dto_scribe_encounter_pb';

import type {
  ConsultationFragment,
  ConsultationStatus,
  ConsultationType,
  Archival,
  Document,
  DocumentSection,
  DocumentStatus,
  DocumentType,
  EncounterAbstract,
  EncounterAnalytics,
  LiteratureReference,
  PersonalInfo,
  StoredAudio,
  Transcript,
  TranscriptorConsultationResponse,
} from './transcriptorTypes';

const CONSULTATION_TYPES_BY_SCHEMA_CODE: Record<string, ConsultationType> = {
  'dental-consultation': 'dental',
  'pediatric-consultation': 'pediatric',
  'medical-consultation': 'medical',
  'coordinator-new-patient': 'coordinator_new_patient',
  'coordinator-follow-up': 'coordinator_follow_up',
  'soap-consultation': 'soap',
};

const ENCOUNTER_STATUS_TO_DOMAIN: Record<EncounterStatus, ConsultationStatus> =
  {
    [EncounterStatus.EncounterStatusInvalidValue]: 'created',
    [EncounterStatus.EncounterStatusAwaitingUpload]: 'created',
    [EncounterStatus.EncounterStatusRecording]: 'recording',
    [EncounterStatus.EncounterStatusProcessingQueued]: 'processing',
    [EncounterStatus.EncounterStatusProcessing]: 'processing',
    [EncounterStatus.EncounterStatusCompleted]: 'succeeded',
    [EncounterStatus.EncounterStatusFailed]: 'failed',
  };

const FRAGMENT_STATUS_TO_DOMAIN: Record<FragmentStatus, ConsultationStatus> = {
  [FragmentStatus.FragmentStatusInvalidValue]: 'created',
  [FragmentStatus.FragmentStatusAwaitingUpload]: 'created',
  [FragmentStatus.FragmentStatusUploaded]: 'processing',
  [FragmentStatus.FragmentStatusRecording]: 'recording',
  [FragmentStatus.FragmentStatusStopped]: 'processing',
  [FragmentStatus.FragmentStatusCompleted]: 'succeeded',
  [FragmentStatus.FragmentStatusFailed]: 'failed',
};

const DOCUMENT_STATUS_TO_DOMAIN: Record<
  EncounterDocumentStatus,
  DocumentStatus
> = {
  [EncounterDocumentStatus.EncounterDocumentStatusInvalidValue]: 'pending',
  [EncounterDocumentStatus.EncounterDocumentStatusPending]: 'pending',
  [EncounterDocumentStatus.EncounterDocumentStatusGenerating]: 'generating',
  [EncounterDocumentStatus.EncounterDocumentStatusSkipped]: 'skipped',
  [EncounterDocumentStatus.EncounterDocumentStatusCompleted]: 'completed',
  [EncounterDocumentStatus.EncounterDocumentStatusFailed]: 'failed',
  [EncounterDocumentStatus.EncounterDocumentStatusEdited]: 'edited',
};

const DOCUMENT_TYPE_TO_DOMAIN: Record<
  EncounterDocumentType,
  DocumentType | null
> = {
  [EncounterDocumentType.EncounterDocumentTypeInvalidValue]: null,
  [EncounterDocumentType.EncounterDocumentTypeSummary]: 'summary',
  [EncounterDocumentType.EncounterDocumentTypeLetter]: 'letter',
  [EncounterDocumentType.EncounterDocumentTypeFollowUpLetter]:
    'follow_up_letter',
  [EncounterDocumentType.EncounterDocumentTypeProtocol]: 'protocol',
  [EncounterDocumentType.EncounterDocumentTypeEvaluation]: 'evaluation',
  [EncounterDocumentType.EncounterDocumentTypeTemplate]: null,
  [EncounterDocumentType.EncounterDocumentTypeLiteratureReview]:
    'literature_review',
};

const toISOString = (
  timestamp?: { toDate: () => Date } | undefined,
): string | undefined => timestamp?.toDate().toISOString();

const emptyToUndefined = (value: string): string | undefined =>
  value === '' ? undefined : value;

const emptyToNull = (value: string): string | null =>
  value === '' ? null : value;

const personalInfoToDomain = (
  info?: PersonalInfoMessage,
): PersonalInfo | null => {
  if (!info) {
    return null;
  }

  return {
    external_id: emptyToNull(info.ExternalID),
    full_name: emptyToNull(info.FullName),
    first_name: emptyToNull(info.FirstName),
    last_name: emptyToNull(info.LastName),
    email: emptyToNull(info.Email),
    date_of_birth: emptyToNull(info.DateOfBirth),
    phone_number: emptyToNull(info.PhoneNumber),
  };
};

const archivalToDomain = (archival?: ArchivalMessage): Archival | null => {
  if (!archival) {
    return null;
  }

  return {
    by_user_id: emptyToUndefined(archival.ByUserID),
    by_api_key_id: emptyToUndefined(archival.ByAPIKeyID),
    at: toISOString(archival.At) ?? '',
  };
};

const abstractToDomain = (
  abstract?: EncounterAbstractMessage,
): EncounterAbstract | null => {
  if (!abstract) {
    return null;
  }

  return {
    status: DOCUMENT_STATUS_TO_DOMAIN[abstract.Status],
    content: emptyToNull(abstract.Content),
    error: abstract.Error ?? null,
    created_at: toISOString(abstract.CreatedAt),
    updated_at: toISOString(abstract.UpdatedAt),
  };
};

const storedAudioToDomain = (
  audio?: StoredAudioMessage,
): StoredAudio | null => {
  if (!audio) {
    return null;
  }

  return {
    url: audio.URL,
    duration: audio.Duration === undefined ? undefined : Number(audio.Duration),
    format: emptyToUndefined(audio.ContentType),
  };
};

const transcriptToDomain = (
  transcript?: TranscriptMessage,
): Transcript | null => {
  if (!transcript) {
    return null;
  }

  return {
    lang: transcript.Lang,
    chunks: transcript.Chunks.map((chunk) => ({
      text: chunk.Text,
      start: chunk.Start,
      end: chunk.End,
      ...(chunk.Speaker && { speaker: chunk.Speaker }),
    })),
  };
};

const fragmentToDomain = (
  fragment: EncounterFragmentMessage,
): ConsultationFragment => ({
  id: fragment.ID,
  status: FRAGMENT_STATUS_TO_DOMAIN[fragment.Status],
  audio: storedAudioToDomain(fragment.Audio),
  transcript: transcriptToDomain(fragment.Transcript),
  error: fragment.Error ?? null,
  created_at: toISOString(fragment.CreatedAt) ?? '',
  updated_at: toISOString(fragment.UpdatedAt) ?? '',
});

const referenceToDomain = (
  reference: LiteratureReferenceMessage,
): LiteratureReference => ({
  index: reference.Index,
  title: reference.Title,
  authors: emptyToNull(reference.Authors),
  year: reference.Year ?? null,
  journal: emptyToNull(reference.Journal),
  abstract: reference.Abstract,
  pmid: emptyToNull(reference.PMID),
  doi: emptyToNull(reference.DOI),
});

const sectionToDomain = (
  section: EncounterDocumentSectionMessage,
): DocumentSection => {
  // UserContent carries only the text, so its author and timestamp have to come
  // from the newest feedback entry.
  const lastEdit = section.EditFeedback.at(-1);

  return {
    code: section.Code,
    title: section.Title,
    category: section.Category,
    content: section.Content === '' ? [] : [{ text: section.Content }],
    user_content:
      section.UserContent === undefined
        ? undefined
        : {
            text: section.UserContent,
            by_user_id: lastEdit && emptyToUndefined(lastEdit.ByUserID),
            updated_at: (lastEdit && toISOString(lastEdit.CreatedAt)) ?? '',
          },
    score: section.Score,
  };
};

export const documentToDomain = (
  document: EncounterDocumentMessage,
): Document => ({
  id: document.ID,
  template_id: emptyToNull(document.TemplateID),
  template_series_id: emptyToNull(document.TemplateSeriesID),
  type: DOCUMENT_TYPE_TO_DOMAIN[document.Type],
  code: document.Code,
  name: document.Name,
  tab_title: emptyToNull(document.TabTitle),
  sections: document.Sections.map(sectionToDomain),
  status: DOCUMENT_STATUS_TO_DOMAIN[document.Status],
  error: document.Error ?? null,
  overall_score: document.OverallScore,
  references: document.References.map(referenceToDomain),
  requires_approval: document.RequiresApproval,
  approved: document.Approved,
  approved_at: toISOString(document.ApprovedAt) ?? null,
  approved_by_user_id: emptyToNull(document.ApprovedByUserID),
  section_overrides: document.SectionOverrides.map((override) => ({
    code: override.Code,
    title: override.Title,
    instructions: override.Instructions,
  })),
});

// Analytics is a schema-defined payload, so the contract carries it as a
// Struct and the shape is whatever the encounter's analytics model produced.
const analyticsToDomain = (
  analytics?: EncounterMessage['Analytics'],
): EncounterAnalytics | null =>
  analytics ? (analytics.toJson() as EncounterAnalytics) : null;

const commonFieldsToDomain = (
  encounter: EncounterMessage | EncounterPreviewMessage,
): TranscriptorConsultationResponse => ({
  id: encounter.ID,
  transcriptor_v2: encounter.TranscriptorV2,
  recording_mode:
    encounter.RecordingMode === RecordingMode.RecordingModeDictation
      ? 'dictation'
      : 'visit',
  dictation_deadline_at: toISOString(encounter.DictationDeadlineAt) ?? null,
  type: CONSULTATION_TYPES_BY_SCHEMA_CODE[encounter.SchemaCode],
  user_id: emptyToUndefined(encounter.UserID),
  organization_id: emptyToUndefined(encounter.OrganizationID),
  patient_id: emptyToNull(encounter.PatientID),
  external_id:
    emptyToUndefined(encounter.ExternalID) ??
    emptyToUndefined(encounter.PatientID),
  name: emptyToNull(encounter.Name),
  lang: encounter.Lang,
  status: ENCOUNTER_STATUS_TO_DOMAIN[encounter.Status],
  error: encounter.Error ?? null,
  schema_id: emptyToNull(encounter.SchemaID),
  schema_code: emptyToNull(encounter.SchemaCode),
  schema_name: emptyToNull(encounter.SchemaName),
  evaluation_overall_score: encounter.EvaluationOverallScore,
  abstract: abstractToDomain(encounter.Abstract),
  patient_info: personalInfoToDomain(encounter.PatientInfo),
  extracted_patient_info: personalInfoToDomain(encounter.ExtractedPatientInfo),
  staff_info: personalInfoToDomain(encounter.StaffInfo),
  archived: archivalToDomain(encounter.Archived),
  your_permissions: encounter.YourPermissions
    ? {
        can_view_evaluation: encounter.YourPermissions.CanViewEvaluation,
        can_archive: encounter.YourPermissions.CanArchive,
        can_restore: encounter.YourPermissions.CanRestore,
        can_delete: encounter.YourPermissions.CanDelete,
      }
    : null,
  started_at: toISOString(encounter.StartedAt) ?? null,
  created_at: toISOString(encounter.CreatedAt) ?? '',
  updated_at: toISOString(encounter.UpdatedAt) ?? '',
});

export const isLiteratureReviewDocument = (document: Document): boolean =>
  document.type === 'literature_review' ||
  Boolean(document.code?.startsWith('consultation_literature_review')) ||
  Boolean(document.code?.startsWith('literature_review'));

// scribe types every seeded follow-up template as a plain letter and even infers
// that type for any code containing follow_up, so the canonical type never
// arrives today and the code is what separates the letters. The type guard is
// load-bearing: coordinator evaluation and summary codes also carry follow_up.
export const isFollowUpDocument = (document: Document): boolean =>
  document.type === 'follow_up_letter' ||
  (document.type === 'letter' && Boolean(document.code?.includes('follow_up')));

// The full encounter has no total; it is the sum the preview reports.
const totalAudioDurationNs = (fragments: ConsultationFragment[]) => {
  const durations = fragments.flatMap((fragment) =>
    fragment.audio?.duration === undefined ? [] : [fragment.audio.duration],
  );
  return durations.length
    ? durations.reduce((total, duration) => total + duration, 0)
    : undefined;
};

export const encounterToDomain = (
  encounter: EncounterMessage = new EncounterMessage(),
): TranscriptorConsultationResponse => {
  const documents = encounter.Documents.map(documentToDomain);
  const fragments = encounter.Fragments.map(fragmentToDomain);

  return {
    ...commonFieldsToDomain(encounter),
    total_audio_duration_ns: totalAudioDurationNs(fragments),
    analytics: analyticsToDomain(encounter.Analytics),
    fragments,
    documents,
    summary: documents.find((document) => document.type === 'summary'),
    evaluation: documents.find((document) => document.type === 'evaluation'),
    follow_up: documents.find(isFollowUpDocument),
    literature_review: documents.find(isLiteratureReviewDocument),
  };
};

export const encounterPreviewToDomain = (
  encounter: EncounterPreviewMessage,
): TranscriptorConsultationResponse => ({
  ...commonFieldsToDomain(encounter),
  total_audio_duration_ns:
    encounter.TotalAudioDurationNS === undefined
      ? undefined
      : Number(encounter.TotalAudioDurationNS),
});
